/**
 * Хто увійшов — для меню користувача і «Мого профілю». Після входу — `GET /api/v1/me` (IAM;
 * user-cabinet.md, розд. 10.5): запит іде після межі першого екрана (perf/first-screen.ts), аватар
 * показує ініціали, щойно ім'я прийшло, а фото — коли воно є (shell/avatar.tsx). Екрани, яким `/me` потрібен для даних (профіль, форма
 * задачі), запитують його самі. Без входу людини немає (`null`).
 */
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useFirstScreenDone } from '../perf/first-screen.ts';
import { fetchMe, platformKeys } from '../platform/api.ts';
import { queryPolicy } from '../platform/query-policy.ts';
import { setRecentOwner } from '../platform/recent.ts';
import { startPrefsSync } from '../prefs/sync.ts';
import { setDraftUser } from '../ui/save/drafts.ts';

export interface ShellUser {
  /** Є лише після входу в платформу: за ним оболонка веде «показано» привітання (UX-38). */
  id?: string;
  name: string;
  initials: string;
  /** `avatar` з `GET /me` (PRF-02): ключ адреси фото, `null` — фото немає (shell/avatar.tsx). */
  avatar: string | null;
  /** Увійшов у платформу: є «Мій профіль». */
  signedIn: boolean;
}

export const meQuery = () =>
  queryOptions({
    queryKey: platformKeys.me,
    queryFn: ({ signal }) => fetchMe(signal),
    ...queryPolicy.reference,
    retry: false,
  });

/** «Олена Петренко» → «ОП»; одне слово — перша літера. */
export function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
}

export function useShellUser(signedIn: boolean): ShellUser | null {
  const firstScreen = useFirstScreenDone();
  const me = useQuery({ ...meQuery(), enabled: signedIn && firstScreen });
  // Власник чернеток форм і недавніх записів — хто увійшов: чернетки й недавні іншого
  // користувача цього браузера зникають (UX-26, UX-04).
  const userId = signedIn ? me.data?.id : undefined;
  useEffect(() => {
    if (!userId) return;
    setDraftUser(userId);
    setRecentOwner(userId);
    // Особисті налаштування з сервера (PRF-01): кеш першого кадру оновиться значеннями профілю.
    startPrefsSync();
  }, [userId]);
  const name = signedIn ? (me.data?.displayName ?? '') : '';
  // Старий сервер `avatar` не віддає — ініціали.
  const avatar = me.data?.avatar ?? null;
  return name && userId
    ? { id: userId, name, initials: initialsOf(name), avatar, signedIn: true }
    : null;
}
